import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

const EditOrganization = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    isActive: true,
    adminEmail: '',
    requiresPayment: false,
    orgType: 'corporate',
    groupsEnabled: false,
  });
  const [subscription, setSubscription] = useState(null);
  const [annualPackages, setAnnualPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    fetchOrganization();
    fetchSubscription();
    fetchAnnualPackages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOrganization = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/organizations/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const org = response.data.data;
      
      setFormData({
        name: org.name,
        slug: org.slug,
        description: org.description || '',
        isActive: org.isActive,
        adminEmail: org.adminEmail || '',
        requiresPayment: org.requiresPayment || false,
        orgType: org.orgType || 'corporate',
        groupsEnabled: org.groupsEnabled || false,
      });
    } catch (error) {
      toast.error('Failed to load organization');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/subscriptions/organization/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubscription(response.data.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  };

  const fetchAnnualPackages = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/pricing-packages?type=annual`
      );
      setAnnualPackages(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch annual packages:', error);
    }
  };

  const handleAssignSubscription = async () => {
    if (!selectedPackageId) {
      toast.error('Please select an annual package');
      return;
    }
    setSubscriptionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/subscriptions`,
        { organizationId: id, pricingPackageId: selectedPackageId, paymentStatus: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription assigned successfully!');
      setSelectedPackageId('');
      fetchSubscription();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to assign subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleDeactivateSubscription = async () => {
    if (!subscription) return;
    setSubscriptionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/subscriptions/${subscription.id}`,
        { isActive: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Subscription deactivated');
      fetchSubscription();
    } catch (error) {
      toast.error('Failed to deactivate subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Don't send adminEmail if it's empty (to avoid changing existing admin to voter)
      const updateData = { ...formData };
      if (!updateData.adminEmail || updateData.adminEmail.trim() === '') {
        delete updateData.adminEmail;
      }
      
      await axios.put(
        `${process.env.REACT_APP_API_URL}/organizations/${id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Organization updated successfully!');
      navigate('/super-admin/organizations');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update organization');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        {/* Card Header */}
        <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1.125rem', color: '#1f2937' }}>
                Edit Organization
              </h5>
            </div>
            <Link
              to="/super-admin/organizations"
              className="btn btn-sm"
              style={{
                backgroundColor: '#f3f4f6',
                border: 'none',
                color: '#6b7280',
                padding: '0.375rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                borderRadius: '0.375rem'
              }}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Back to Organizations
            </Link>
          </div>
        </div>

        {/* Card Body */}
        <div className="card-body" style={{ padding: '2rem' }}>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-8">
                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    style={{ fontSize: '0.875rem' }}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                    Slug *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({ ...formData, slug: e.target.value })
                    }
                    placeholder="e.g. acme-corp"
                    required
                    style={{ fontSize: '0.875rem' }}
                  />
                  <small className="text-muted">
                    URL-friendly identifier (lowercase, no spaces)
                  </small>
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    rows="4"
                    style={{ fontSize: '0.875rem' }}
                  ></textarea>
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                    Admin Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    value={formData.adminEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, adminEmail: e.target.value })
                    }
                    placeholder="admin@organization.com"
                    style={{ fontSize: '0.875rem' }}
                  />
                  <small className="text-muted">
                    Change the admin email to reassign organization ownership
                  </small>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="isActive" style={{ fontSize: '0.875rem', color: '#374151' }}>
                      Active
                    </label>
                  </div>
                  <small className="text-muted">
                    Inactive organizations will not be visible to users
                  </small>
                </div>

                <div className="mb-4">
                  <label className="form-label" style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                    Organization Type *
                  </label>
                  <select
                    className="form-select"
                    value={formData.orgType}
                    onChange={(e) => setFormData({ ...formData, orgType: e.target.value })}
                    style={{ fontSize: '0.875rem' }}
                  >
                    <option value="corporate">Corporate (email-based members)</option>
                    <option value="school">School (ID-based members)</option>
                  </select>
                  <small className="text-muted">
                    {formData.orgType === 'school'
                      ? 'School mode: voters can be added without email, voter IDs are auto-generated, group management enabled'
                      : 'Corporate mode: all members require email addresses for invitations'}
                  </small>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="groupsEnabled"
                      checked={formData.groupsEnabled}
                      onChange={(e) =>
                        setFormData({ ...formData, groupsEnabled: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="groupsEnabled" style={{ fontSize: '0.875rem', color: '#374151' }}>
                      Enable Groups
                    </label>
                  </div>
                  <small className="text-muted">
                    {formData.groupsEnabled
                      ? 'Classes and departments can be created and assigned to voters'
                      : 'Group management is disabled for this organization'}
                  </small>
                </div>

                <div className="mb-4">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="requiresPayment"
                      checked={formData.requiresPayment}
                      onChange={(e) =>
                        setFormData({ ...formData, requiresPayment: e.target.checked })
                      }
                    />
                    <label className="form-check-label" htmlFor="requiresPayment" style={{ fontSize: '0.875rem', color: '#374151' }}>
                      Require Payment
                    </label>
                  </div>
                  <small className="text-muted">
                    {formData.requiresPayment
                      ? 'Organization must select and pay for pricing packages per election'
                      : 'Organization has unlimited access to all features (free/sponsored)'}
                  </small>
                </div>

                {/* Annual Subscription Section */}
                {formData.requiresPayment && (
                  <div className="mb-4 p-3" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem' }}>
                    <h6 style={{ fontWeight: '600', fontSize: '0.875rem', color: '#15803d', marginBottom: '0.75rem' }}>
                      <i className="bi bi-calendar-check me-2"></i>Annual Subscription
                    </h6>
                    {subscription ? (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="badge bg-success">Active</span>
                          <strong style={{ fontSize: '0.875rem' }}>{subscription.pricingPackage?.name}</strong>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#4b5563' }}>
                          <div>Expires: {new Date(subscription.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
                          <div>Days remaining: {Math.max(0, Math.ceil((new Date(subscription.expiryDate) - new Date()) / (1000 * 60 * 60 * 24)))}</div>
                        </div>
                        <button
                          onClick={handleDeactivateSubscription}
                          className="btn btn-sm btn-outline-danger mt-2"
                          disabled={subscriptionLoading}
                          style={{ fontSize: '0.75rem' }}
                        >
                          {subscriptionLoading ? 'Processing...' : 'Deactivate Subscription'}
                        </button>
                      </div>
                    ) : (
                      <div>
                        <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                          No active subscription. Assign an annual package to allow unlimited elections.
                        </p>
                        {annualPackages.length > 0 ? (
                          <div className="d-flex gap-2 align-items-end">
                            <div style={{ flex: 1 }}>
                              <select
                                className="form-select form-select-sm"
                                value={selectedPackageId}
                                onChange={(e) => setSelectedPackageId(e.target.value)}
                                style={{ fontSize: '0.8rem' }}
                              >
                                <option value="">Select annual package...</option>
                                {annualPackages.map(pkg => (
                                  <option key={pkg.id} value={pkg.id}>
                                    {pkg.name} — {new Intl.NumberFormat('en-US', { style: 'currency', currency: pkg.currency || 'USD', minimumFractionDigits: 0 }).format(pkg.price)} ({pkg.durationDays} days)
                                  </option>
                                ))}
                              </select>
                            </div>
                            <button
                              onClick={handleAssignSubscription}
                              className="btn btn-sm btn-success"
                              disabled={subscriptionLoading || !selectedPackageId}
                              style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                              {subscriptionLoading ? 'Assigning...' : 'Assign Subscription'}
                            </button>
                          </div>
                        ) : (
                          <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                            No annual packages available. Create one in Pricing Packages first.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn"
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      color: 'white',
                      padding: '0.5rem 1.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      borderRadius: '0.375rem'
                    }}
                  >
                    Save Changes
                  </button>
                  <Link
                    to="/super-admin/organizations"
                    className="btn"
                    style={{
                      backgroundColor: '#f3f4f6',
                      border: 'none',
                      color: '#6b7280',
                      padding: '0.5rem 1.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      borderRadius: '0.375rem'
                    }}
                  >
                    Cancel
                  </Link>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card" style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb' }}>
                  <div className="card-body">
                    <h6 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>
                      Organization Info
                    </h6>
                    <div className="mb-3">
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                        Organization ID
                      </small>
                      <code style={{ fontSize: '0.75rem', color: '#6b7280' }}>{id}</code>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                        Status
                      </small>
                      <span className={`badge ${formData.isActive ? 'bg-success' : 'bg-secondary'}`}>
                        {formData.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted d-block" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                        Type
                      </small>
                      <span className={`badge ${formData.orgType === 'school' ? 'bg-info' : 'bg-primary'}`}>
                        {formData.orgType === 'school' ? '🏫 School' : '🏢 Corporate'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditOrganization;
